import * as i18n from "i18next";
import {Options} from "i18next";
import * as XHR from "i18next-xhr-backend";
import * as React from 'react';
import * as ReactDom from 'react-dom';
import {Redirect, RouteComponentProps} from "react-router";
import {BrowserRouter, Route, Switch} from "react-router-dom";
import {Divider, Icon, Loader, Menu, Popup} from "semantic-ui-react";
import '../sass/main.scss';
import {DutyMenuItem, DutyState} from "./components/dutyMenuItem";
import {AboutUsModal} from "./components/modals/aboutUsModal";
import {ApparatusSignOutModal} from "./components/modals/apparatusSignoutModal";
import {ContactSupportModal} from "./components/modals/contactSupportModal";
import {PermissionsModal} from "./components/modals/permissionsModal";
import {ReleaseNotesModal} from "./components/modals/releaseNotesModal";
import {NotificationMenu} from "./components/notificationMenu";
import {config} from "./config";
import {Login} from "./login";
import {BryxNotification, BryxNotificationType} from "./models/bryxNotification";
import {Stream, StreamingStatus} from "./models/stream";
import {BadBrowser} from "./pages/badBrowser";
import {EulaPage} from "./pages/eulaPage";
import {JobsPage} from "./pages/jobs/jobsPage";
import {MapPage} from "./pages/map/mapPage";
import {MessagesPage} from "./pages/messages/messagesPage";
import {SettingsModal, SettingsTabType} from "./pages/settings/settingsModal";
import {StreamPlaybackCommand, StreamsPage} from "./pages/streams/streamsPage";
import {RouterMenuItem} from "./routedItems";
import {BryxApi} from "./utils/bryxApi";
import {BryxLocal} from "./utils/bryxLocal";
import {BryxWebSocket, BryxWebSocketState, BryxWebSocketStateObserver} from "./utils/bryxWebSocket";
import {DeviceUtils} from "./utils/deviceUtils";
import {JobManager} from "./utils/jobManager";
import {LocationManager} from "./utils/locationManager";
import {MessageManager} from "./utils/messageManager";
import {NotificationManager, NotificationManagerObserver, NotificationStatus} from "./utils/notificationManager";
import {PreferenceManager} from "./utils/preferenceManager";
import {ReleaseNotesUtils} from "./utils/releaseNotesUtils";
import {SessionManager} from "./utils/sessionManager";
import {UpdateManager, UpdateManagerObserver, UpdateStatus} from "./utils/updateManager";

type SignOutType = "standard" | "manual" | "forced" | "passwordChanged";
type OverlayContentKey =
    "none"
    | "menu"
    | "duty"
    | "notifications"
    | "settings"
    | "contactSupport"
    | "permissions"
    | "releaseNotes"
    | "about"
    | "apparatusSignOut";

interface MainState {
    isLoading: boolean;
    status: { key: "signedIn" } |
        { key: "signedOut", type: SignOutType };
    notificationStatus: NotificationStatus;
    overlayContent: OverlayContentKey;
    settingsTabType: SettingsTabType;
    dutyState: DutyState;
    streamStatus: StreamingStatus;
    webSocketState: BryxWebSocketState;
    updateStatus: UpdateStatus;
}

export class Main extends React.Component<RouteComponentProps<any>, MainState> implements NotificationManagerObserver, BryxWebSocketStateObserver, UpdateManagerObserver {
    private activeStream: HTMLAudioElement | null = null;
    private static readonly minimumSplashTime = 2.0 * 1000;

    constructor(props: any, context: any) {
        super(props, context);

        BryxApi.onUnauthenticated = () => {
            config.info("User is being force signed out");
            Main.resetManagers();
            this.setState({
                isLoading: false,
                status: {key: "signedOut", type: "forced"},
            });
        };

        i18n
            .use(XHR)
            .init({
                lng: BryxLocal.getItem<string>("locale") || "en-US",
                backend: {
                    loadPath: "/resources/locales/{{lng}}.json",
                },
                fallbackLng: "en",
            } as Options, () => {
                this.loadSession();
            });

        this.state = {
            isLoading: BryxLocal.isSignedIn(),
            status: BryxLocal.isSignedIn() ? {key: "signedIn"} : {key: "signedOut", type: "standard"},
            notificationStatus: NotificationManager.shared.status,
            overlayContent: Main.defaultOverlayContent(),
            settingsTabType: SettingsModal.allTabs()[0],
            dutyState: {key: "initial"},
            streamStatus: {key: "unselected"},
            webSocketState: BryxWebSocket.shared.state,
            updateStatus: UpdateManager.shared.updateStatus,
        };
    }

    private static defaultOverlayContent(): OverlayContentKey {
        if (PermissionsModal.anyIncompleteStages()) {
            return "permissions";
        } else if (ReleaseNotesUtils.getStatus() == "show") {
            return "releaseNotes";
        } else {
            return "none";
        }
    }

    componentDidMount() {
        NotificationManager.shared.registerObserver(this);
        BryxWebSocket.shared.registerStateObserver(this);
        UpdateManager.shared.registerObserver(this);
    }

    private onSelectStream(stream: Stream) {
        if (this.activeStream != null) {
            this.activeStream.pause();
            this.activeStream.currentTime = 0;
            this.activeStream.src = "";
            this.activeStream = null;
        }
        this.setState({
            streamStatus: {
                key: "selected",
                stream: stream,
                playback: {key: "playing", loading: true},
            },
        });
        const activeStream = new Audio(stream.url);
        this.activeStream = activeStream;
        activeStream.play().then(() => {
            this.setState({
                streamStatus: {
                    key: "selected",
                    stream: stream,
                    playback: {key: "playing", loading: false},
                },
            });
        }).catch(reason => {
            if (reason.name == "AbortError") {
                config.info(`User paused stream@${stream.id} while still loading`);
            } else {
                config.warn(`Failed to load stream${stream.id}: ${reason}`);
                this.setState({
                    streamStatus: {
                        key: "selected",
                        stream: stream,
                        playback: {key: "error"},
                    },
                });
            }
        });
    }

    private onStreamPlaybackCommand(command: StreamPlaybackCommand) {
        if (this.state.streamStatus.key != "selected") {
            config.error("Failed to change playback; no stream selected");
            return;
        }
        switch (command) {
            case "play":
                // Resume logic is the same as selection
                this.onSelectStream(this.state.streamStatus.stream);
                break;
            case "pause":
                if (this.activeStream != null) {
                    this.activeStream.pause();
                    this.activeStream.currentTime = 0;
                    this.activeStream.src = "";
                    this.activeStream = null;
                } else {
                    config.error("Failed to pause null stream");
                }
                this.setState(prevState => {
                    if (prevState.streamStatus.key == "selected") {
                        prevState.streamStatus.playback = {key: "paused"};
                    }
                    return prevState;
                });
                break;
            case "stop":
                if (this.activeStream != null) {
                    this.activeStream.pause();
                    this.activeStream.currentTime = 0;
                    this.activeStream.src = "";
                    this.activeStream = null;
                }
                // User can stop a stream from any playback state
                this.setState({streamStatus: {key: "unselected"}});
                break;
        }
    }

    componentWillUnmount() {
        NotificationManager.shared.unregisterObserver(this);
        BryxWebSocket.shared.unregisterStateObserver(this);
        UpdateManager.shared.unregisterObserver(this);
    }

    private loadSession() {
        if (BryxLocal.isSignedIn()) {
            const preSessionTime = Date.now();
            BryxApi.session((result) => {
                const postSessionTime = Date.now();
                // If session fails on a 401, `BryxApi.onUnauthenticated` will be called and will force sign out.
                // Any other errors are non-recoverable.
                if (result.success == true) {
                    config.info("Successfully updated session");
                    if (BryxLocal.getShowEula() != true) {
                        JobManager.shared.startLoadingJobs();
                        MessageManager.shared.startMonitoringGroups();
                        NotificationManager.shared.startLoadingNotifications();
                        UpdateManager.shared.start();
                        SessionManager.shared.start();
                        LocationManager.shared.startWatch();
                        setTimeout(() => {
                            this.setState({
                                isLoading: false,
                                status: {key: "signedIn"},
                                dutyState: {key: "active", duty: result.value.dutyStatus, subState: {key: "ready"}},
                            });
                        }, Math.max(Main.minimumSplashTime - (postSessionTime - preSessionTime), 0));
                    } else {
                        this.props.history.push("/eula", {
                            from: this.props.location,
                        });
                    }
                } else {
                    config.warn(`Failed to update session: ${result.debugMessage}`);
                }
            });
        }
    }

    private signoutComplete(type: SignOutType) {
        Main.resetManagers();
        this.setState({
            status: {key: "signedOut", type: type},
        });
    }

    private openOverlay(key: OverlayContentKey) {
        if (this.state.overlayContent == "none") {
            this.setState({overlayContent: key});
        } else if (this.state.overlayContent != key) {
            // If a popup is already open, we need close the existing one before opening the new one.
            // Otherwise, `onClose` will immediately be called for the new overlay.
            this.setState({overlayContent: "none"}, () => this.setState({overlayContent: key}));
        }
    }

    private closeOverlay() {
        this.setState({
            overlayContent: Main.defaultOverlayContent(),
        });
    }

    public static resetManagers() {
        JobManager.shared.reset(false);
        MessageManager.shared.reset();
        NotificationManager.shared.reset();
        BryxLocal.clear();
        PreferenceManager.shared.reset();
        UpdateManager.shared.reset();
        SessionManager.shared.reset();
        LocationManager.shared.reset();
    }

    private signOut() {
        if (BryxLocal.isApparatus()) {
            this.setState({
                overlayContent: "apparatusSignOut",
            });
        } else {
            BryxApi.signOut(result => {
                if (result.success == true) {
                    config.info("User successfully signed out");
                    this.signoutComplete("manual");
                } else {
                    config.warn(`Failed to sign out: ${result.debugMessage}`);
                }
            });
        }
    }

    // NotificationManagerObserver Functions

    notificationManagerDidUpdateStatus(status: NotificationStatus): void {
        this.setState({notificationStatus: status});
    }

    notificationManagerDidReceiveClick(notification: BryxNotification): void {
        window.focus();
        switch (notification.content.type) {
            case BryxNotificationType.job:
                this.props.history.push(`/jobs/${notification.content.jobId}`);
                break;
            case BryxNotificationType.supplemental:
                this.props.history.push(`/jobs/${notification.content.jobId}`);
                break;
            case BryxNotificationType.message:
                this.props.history.push(`/messaging/${notification.content.groupId}`);
                break;
        }
    }

    // BryxWebSocketStateObserver Functions

    websocketStateDidChange(state: BryxWebSocketState) {
        this.setState({webSocketState: state});
    }

    // UpdateManagerObserver Functions

    updateManagerDidChangeUpdateStatus(updateStatus: UpdateStatus) {
        this.setState({updateStatus: updateStatus});
    }

    render() {
        if (this.state.isLoading) {
            return (
                <div className="index-loading">
                    <img id="loadingIcon" src="/resources/assets/logo_white.png" className="centerVertically"/>
                </div>
            );
        }

        if (this.state.status.key == "signedOut") {
            return (
                <Redirect to={{
                    pathname: "/login",
                    state: {
                        type: this.state.status.type,
                        from: this.state.status.type != "manual" ? this.props.location : null,
                    },
                }}/>
            );
        }

        // Setting dark or light mode based on localStorage
        if (localStorage.getItem('darkMode') == 'true') {
            document.body.classList.add('dark');
        } else {
            document.body.classList.add('light');
        }

        return (
            <div id="mainRoot">
                <Menu
                    className="navigation-menu"
                    attached="top"
                    color="red"
                    inverted
                >
                    <Menu.Item>
                        <img className="logo" src={"/resources/assets/logo_white.png"}/>
                        <span className="siteName">{i18n.t("branding.siteName")}</span>
                    </Menu.Item>

                    <RouterMenuItem to="/jobs">{i18n.t("nav.jobs")}</RouterMenuItem>
                    <RouterMenuItem to="/map">{i18n.t("nav.map")}</RouterMenuItem>
                    <RouterMenuItem to="/messaging">{i18n.t("nav.messages")}</RouterMenuItem>
                    <RouterMenuItem to="/streams">
                        <span
                            className={this.state.streamStatus.key == "selected" && this.state.streamStatus.playback.key == "playing" ?
                                "animateFlicker" : undefined}>
                            {i18n.t("nav.streams")}
                        </span>
                    </RouterMenuItem>

                    <Menu.Menu position="right">
                        {this.state.webSocketState == BryxWebSocketState.reconnecting ? (
                            <Menu.Item>
                                <Popup trigger={
                                    <div className="reconnecting-popup">
                                        <Loader
                                            className="reconnecting-loader"
                                            active
                                            inline
                                            inverted
                                            size="small"
                                        />
                                        {i18n.t("general.reconnecting")}
                                    </div>
                                }>
                                    {i18n.t("general.reconnectingToBryx")}
                                </Popup>
                            </Menu.Item>
                        ) : null}
                        <DutyMenuItem
                            open={this.state.overlayContent == "duty"}
                            onOpen={() => this.openOverlay("duty")}
                            onClose={this.closeOverlay.bind(this)}
                            dutyState={this.state.dutyState}
                            onUpdateDutyState={state => this.setState({dutyState: state})}
                        />
                        <NotificationMenu
                            open={this.state.overlayContent == "notifications"}
                            onOpen={() => this.openOverlay("notifications")}
                            onClose={this.closeOverlay.bind(this)}
                            status={this.state.notificationStatus}
                            onClickSettings={() => this.setState({
                                settingsTabType: SettingsTabType.alerts,
                            }, () => this.openOverlay("settings"))}
                        />
                        <Popup
                            id="settingsMenu"
                            on="click"
                            position="bottom right"
                            open={this.state.overlayContent == "menu"}
                            onOpen={() => this.openOverlay("menu")}
                            onClose={this.closeOverlay.bind(this)}
                            trigger={
                                <Menu.Item>
                                    <Icon.Group
                                        className="link"
                                        size='big'
                                        style={{opacity: this.state.updateStatus.key == "updateAvailable" ? 1 : undefined}}
                                    >
                                        <Icon name='setting' style={{margin: 0}}/>
                                        {this.state.updateStatus.key == "updateAvailable" ?
                                            <Icon
                                                corner name='circle' color="orange"
                                                style={{fontSize: "10px"}}
                                            /> : null}
                                    </Icon.Group>
                                </Menu.Item>
                            }>
                            <Popup.Content>
                                {this.state.updateStatus.key == "updateAvailable" ? (
                                    <div>
                                        <div
                                            className="popupItem update-available"
                                            onClick={() => {
                                                if (ReleaseNotesUtils.getStatus() == "hide") {
                                                    ReleaseNotesUtils.setStatus("show");
                                                }
                                                UpdateManager.shared.update();
                                            }}
                                        >
                                            <Icon corner name='circle' color="orange"/>
                                            <span className="update-available-text">
                                                {i18n.t("updates.updateAvailable")}
                                            </span>
                                        </div>
                                        <Divider style={{margin: 0}}/>
                                    </div>
                                ) : null}
                                <div className="popupItem" onClick={() => this.openOverlay("settings")}>
                                    {i18n.t("nav.settings.settings")}
                                </div>
                                <div
                                    className="popupItem"
                                    onClick={() => this.openOverlay("contactSupport")}
                                >
                                    {i18n.t("contactSupport.header")}
                                </div>
                                <div
                                    className="popupItem"
                                    onClick={() => this.openOverlay("about")}
                                >
                                    {i18n.t("about.settingsItem")}
                                </div>
                                <div
                                    className="popupItem"
                                    onClick={this.signOut.bind(this)}
                                >
                                    {i18n.t("nav.settings.signOut")}
                                </div>
                            </Popup.Content>
                        </Popup>
                    </Menu.Menu>
                </Menu>

                <ApparatusSignOutModal
                    open={this.state.overlayContent == "apparatusSignOut"}
                    onSignOut={() => this.signoutComplete("manual")}
                    onCancel={this.closeOverlay.bind(this)}
                />
                <AboutUsModal
                    open={this.state.overlayContent == "about"}
                    onClose={this.closeOverlay.bind(this)}
                    onClickReleaseNotes={() => this.openOverlay("releaseNotes")}
                />
                <ContactSupportModal
                    open={this.state.overlayContent == "contactSupport"}
                    onClose={this.closeOverlay.bind(this)}
                />
                <SettingsModal
                    open={this.state.overlayContent == "settings"}
                    currentTab={this.state.settingsTabType}
                    onTabChange={t => this.setState({settingsTabType: t})}
                    onPasswordChanged={() => this.signoutComplete("passwordChanged")}
                    onClose={this.closeOverlay.bind(this)}
                />
                <PermissionsModal
                    open={this.state.overlayContent == "permissions"}
                    onClose={this.closeOverlay.bind(this)}
                />
                <ReleaseNotesModal
                    open={this.state.overlayContent == "releaseNotes"}
                    onClose={this.closeOverlay.bind(this)}
                />

                <div id="content-wrapper">
                    <Switch>
                        <Route path="/jobs/:jobId?/:tabId?/:contentId?" render={(props) => (
                            <JobsPage {...props} />
                        )}/>
                        <Route exact path="/map" render={(props) => (
                            <MapPage {...props}/>
                        )}/>
                        <Route exact path="/messaging/:groupId?" render={(props) => (
                            <MessagesPage {...props}/>
                        )}/>
                        <Route exact path="/streams" render={(props) => (
                            <StreamsPage
                                streams={BryxLocal.getStreams()}
                                playStatus={this.state.streamStatus}
                                onSelect={this.onSelectStream.bind(this)}
                                onPlaybackCommand={this.onStreamPlaybackCommand.bind(this)}
                                {...props}
                            />)}
                        />
                        <Redirect to="/jobs"/>
                    </Switch>
                </div>
            </div>
        );
    }
}

export class MainRouter extends React.Component<any, any> {
    render() {
        return (
            <BrowserRouter>
                <Switch>
                    <Route path="/unsupported-browser" component={BadBrowser}/>
                    <Route path="/eula" component={EulaPage}/>
                    <Route render={() => {
                        if (!DeviceUtils.deviceIsSupported) {
                            return <Redirect to="/unsupported-browser"/>;
                        } else {
                            return (
                                <Switch>
                                    <Route path="/login" component={Login}/>
                                    <Route component={Main}/>
                                </Switch>
                            );
                        }
                    }}/>
                </Switch>
            </BrowserRouter>
        );
    }
}

window.onerror = (message: string, filename?: string, lineno?: number, colno?: number, error?: Error) => {
    config.error(message, filename, lineno, colno, error);
};

ReactDom.render(
    <MainRouter/>,
    document.getElementById("content"),
);
